/**
 * Arbre des séquences + drill joueurs pour une donne d'un différé (mode unitaire).
 */

import { useCallback, useEffect, useState } from 'react';
import { BACKEND_URL } from '../../config';
import DiffereStatsCardChip from './DiffereStatsCardChip';
import DiffereStatsDealDetailModal from './DiffereStatsDealDetailModal';
import type { EquivMode } from './DiffereStatsEquivModeToggle';
import {
  type DiffereStatsDealDetail,
  fetchDiffereStatsDealDetail,
  formatPerDealRankLabel,
} from '../../utils/differeStatsReplay';
import {
  PlayTreeSankeyInteractive,
  type PlayTreeNode,
  type PlayTreePlayerMatch,
} from './DiffereStatsPlayTreeDrill';
import type { DecisionFlowSelection } from './DiffereStatsDecisionFlow';
import {
  buildPlayTreePathLabelMap,
  formatPlayTreePathLabelsFromHumanCards,
  formatPlayTreePathLabelsFromRoot,
  humanCardsByTrickFromPlayerMatches,
  playTreePathKey,
  type PlayTreePathLabelInfo,
} from './differeStatsTreeLabel';

function adminFetch(url: string, pseudo: string) {
  const separator = url.includes('?') ? '&' : '?';
  return fetch(`${url}${separator}pseudo=${encodeURIComponent(pseudo)}`);
}

function playTreeQuery(
  topX: number,
  depth: number,
  groupEquiv: boolean,
  equivRequireOthers: boolean,
  equivRequireWinner: boolean,
  lowRankLimit: number,
  contractTypes: string,
): string {
  const params: Record<string, string> = {
    topX: String(topX),
    depth: String(depth),
    groupEquiv: String(groupEquiv),
    equivRequireOthers: equivRequireOthers ? '1' : '0',
    equivRequireWinner: equivRequireWinner ? '1' : '0',
    contractTypes,
  };
  if (groupEquiv) params.lowRankLimit = String(lowRankLimit);
  return new URLSearchParams(params).toString();
}

export default function DiffereStatsSingleDealDrill({
  sessionPseudo,
  differeId,
  dealIndex,
  dealLabel,
  topX,
  depth,
  groupEquiv,
  equivMode,
  equivRequireOthers,
  equivRequireWinner,
  lowRankLimit,
  contractTypes,
  consensusPathKeys = [],
  consensusDepth = 0,
  consensusBranchSize = 0,
  consensusStopReason,
  differeMeta,
}: {
  sessionPseudo: string;
  differeId: number;
  dealIndex: number;
  dealLabel: string;
  topX: number;
  depth: number;
  groupEquiv: boolean;
  equivMode: EquivMode;
  equivRequireOthers: boolean;
  equivRequireWinner: boolean;
  lowRankLimit: number;
  contractTypes: string;
  /** Préfixe consensus (Prof. adapt.) — clés arbre. */
  consensusPathKeys?: string[];
  consensusDepth?: number;
  consensusBranchSize?: number;
  consensusStopReason?: string;
  differeMeta?: { type?: string | null; debut?: string | null } | null;
}) {
  const [playTreeRoot, setPlayTreeRoot] = useState<PlayTreeNode | null>(null);
  const [treeMaxDepth, setTreeMaxDepth] = useState(18);
  const [pathLabelByKey, setPathLabelByKey] = useState<Map<string, PlayTreePathLabelInfo>>(() => new Map());
  const [totalSequences, setTotalSequences] = useState(0);
  const [loadingTree, setLoadingTree] = useState(false);
  const [treeError, setTreeError] = useState<string | null>(null);

  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [selectedEcartKey, setSelectedEcartKey] = useState<string | null>(null);
  const [flowSelectionLabel, setFlowSelectionLabel] = useState('');
  /** topX élargi quand la sélection vient du flux en population « tous ». */
  const [flowDrillTopX, setFlowDrillTopX] = useState<number | null>(null);
  const [matches, setMatches] = useState<PlayTreePlayerMatch[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [playersError, setPlayersError] = useState<string | null>(null);

  const [studiedDetail, setStudiedDetail] = useState<DiffereStatsDealDetail | null>(null);
  const [compareDetail, setCompareDetail] = useState<DiffereStatsDealDetail | null>(null);
  const [detailHypothesisCards, setDetailHypothesisCards] = useState<number[] | null>(null);
  const [detailTrickFocus, setDetailTrickFocus] = useState<number | null>(null);
  const [detailDiffSeatKeys, setDetailDiffSeatKeys] = useState<Set<string> | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const treeQuery = playTreeQuery(
    topX, depth, groupEquiv, equivRequireOthers, equivRequireWinner, lowRankLimit, contractTypes,
  );

  const clearSelection = useCallback(() => {
    setSelectedPath([]);
    setSelectedEcartKey(null);
    setFlowSelectionLabel('');
    setFlowDrillTopX(null);
  }, []);

  /** Les vues Arbre et Liste ignorent l'écart : le sélectionner remet ce filtre à zéro. */
  const handleSelectPath = useCallback((path: string[]) => {
    setSelectedPath(path);
    setSelectedEcartKey(null);
    setFlowSelectionLabel('');
    setFlowDrillTopX(null);
  }, []);

  const handleFlowSelect = useCallback((next: DecisionFlowSelection | null) => {
    setSelectedPath(next?.pathKeys ?? []);
    setSelectedEcartKey(next?.ecartKey ?? null);
    setFlowSelectionLabel(next?.label ?? '');
    setFlowDrillTopX(next?.drillTopX ?? null);
  }, []);

  useEffect(() => {
    clearSelection();
    setMatches([]);
    setPlayersError(null);
    let cancelled = false;
    setLoadingTree(true);
    setTreeError(null);
    setPlayTreeRoot(null);
    void adminFetch(
      `${BACKEND_URL}/api/admin/stats/differe/${differeId}/deal/${dealIndex}/play-tree?${treeQuery}`,
      sessionPseudo,
    )
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Erreur arbre des séquences');
        if (!cancelled) {
          setPlayTreeRoot(data.root ?? null);
          setTotalSequences(data.totalSequences ?? 0);
          setTreeMaxDepth(data.params?.depth ?? 18);
          setPathLabelByKey(buildPlayTreePathLabelMap(data.pathSummaries ?? []));
        }
      })
      .catch(e => {
        if (!cancelled) {
          setTreeError(e instanceof Error ? e.message : String(e));
          setPlayTreeRoot(null);
          setTotalSequences(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTree(false);
      });
    return () => { cancelled = true; };
  }, [differeId, dealIndex, treeQuery, sessionPseudo, depth]);

  const selectedPathLabel = (() => {
    if (flowSelectionLabel) return flowSelectionLabel;
    if (selectedPath.length === 0) return '';
    if (matches.length > 0) {
      return formatPlayTreePathLabelsFromHumanCards(
        humanCardsByTrickFromPlayerMatches(matches, selectedPath.length),
      );
    }
    const info = pathLabelByKey.get(playTreePathKey(selectedPath));
    if (info) return formatPlayTreePathLabelsFromHumanCards(info.humanCardsByTrick);
    return playTreeRoot
      ? formatPlayTreePathLabelsFromRoot(playTreeRoot, selectedPath)
      : selectedPath.join(' → ');
  })();

  useEffect(() => {
    if (selectedPath.length === 0 && !selectedEcartKey) {
      setMatches([]);
      setPlayersError(null);
      return;
    }
    let cancelled = false;
    setLoadingPlayers(true);
    setPlayersError(null);
    // Le flux peut être en « tous les joueurs » alors que l'arbre reste borné au top-X
    // de la page : sans aligner l'effectif, un segment plein côté flux renvoie une liste vide.
    const drillTopX = flowDrillTopX ?? topX;
    const q = new URLSearchParams(treeQuery);
    q.set('topX', String(drillTopX));
    q.set('path', selectedPath.map(k => encodeURIComponent(k)).join(','));
    if (selectedEcartKey) q.set('ecartKey', selectedEcartKey);
    void adminFetch(
      `${BACKEND_URL}/api/admin/stats/differe/${differeId}/deal/${dealIndex}/play-tree/players?${q}`,
      sessionPseudo,
    )
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Erreur chargement joueurs');
        if (!cancelled) setMatches(data.matches ?? []);
      })
      .catch(e => {
        if (!cancelled) {
          setPlayersError(e instanceof Error ? e.message : String(e));
          setMatches([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPlayers(false);
      });
    return () => { cancelled = true; };
  }, [selectedPath, selectedEcartKey, flowDrillTopX, topX, differeId, dealIndex, treeQuery, sessionPseudo]);

  const openDealDetail = useCallback(async (m: PlayTreePlayerMatch) => {
    setLoadingDetail(true);
    setDetailError(null);
    setStudiedDetail(null);
    setCompareDetail(null);
    setDetailDiffSeatKeys(null);
    try {
      const data = await fetchDiffereStatsDealDetail({
        sessionPseudo,
        topX,
        contractTypes,
        equivMode,
        lowRankLimit,
        differeId: m.differeId,
        resultId: m.resultId,
        dealIndex: m.dealIndex,
      });
      setStudiedDetail(data);
      setDetailHypothesisCards(
        selectedPath.length > 0 ? m.humanCards.slice(0, selectedPath.length) : null,
      );
      setDetailTrickFocus(null);
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingDetail(false);
    }
  }, [sessionPseudo, topX, contractTypes, equivMode, lowRankLimit, selectedPath.length]);

  const closeDealDetailModal = useCallback(() => {
    setStudiedDetail(null);
    setCompareDetail(null);
    setDetailHypothesisCards(null);
    setDetailTrickFocus(null);
    setDetailDiffSeatKeys(null);
    setDetailError(null);
  }, []);

  if (loadingTree) {
    return <p className="admin-muted">Chargement de l&apos;arbre des séquences…</p>;
  }
  if (treeError) {
    return <p className="admin-error-text">{treeError}</p>;
  }
  if (!playTreeRoot || totalSequences === 0) {
    return (
      <p className="admin-muted">
        Aucune séquence dans le top-{topX} pour {dealLabel} (filtres contrats / plis).
      </p>
    );
  }

  return (
    <>
      <h4>
        Arbre des séquences — {dealLabel}{' '}
        (top-{topX}, {totalSequences} joueurs, liste {depth} plis / arbre {treeMaxDepth},{' '}
        {groupEquiv ? `équiv. lowRank≤${lowRankLimit}` : 'brut'})
      </h4>
      <p className="admin-muted differe-stats-hint">
        Dépliez pli par pli jusqu&apos;aux feuilles (effectif 1 ou fin de donne). Bouton Joueurs pour le drill-down.
        L&apos;effectif max en liste (tri Effectif total, profondeur {depth}) doit égaler « Seq. max » du tableau
        si topX / depth / équivalence sont identiques.
      </p>
      <PlayTreeSankeyInteractive
        root={playTreeRoot}
        maxDepth={treeMaxDepth}
        listDepth={depth}
        selectedPath={selectedPath}
        onSelectPath={handleSelectPath}
        pathLabelByKey={pathLabelByKey}
        consensusPathKeys={consensusPathKeys}
        consensusDepth={consensusDepth}
        consensusBranchSize={consensusBranchSize}
        consensusStopReason={consensusStopReason}
        flowContext={{
          sessionPseudo,
          differeId,
          dealIndex,
          dealLabel,
          topX,
          groupEquiv,
          lowRankLimit,
          contractTypes,
          selection: selectedPath.length > 0 || selectedEcartKey
            ? {
              pathKeys: selectedPath,
              ecartKey: selectedEcartKey,
              label: flowSelectionLabel,
              drillTopX: flowDrillTopX ?? topX,
            }
            : null,
          onSelect: handleFlowSelect,
        }}
      />

      {(selectedPath.length > 0 || selectedEcartKey) && (
        <section className="differe-stats-drill-players admin-card">
          <h5>
            Joueurs — {selectedPathLabel}
            <button type="button" className="admin-btn admin-btn-sm" onClick={clearSelection}>
              Fermer
            </button>
          </h5>
          {loadingPlayers && <p className="admin-muted">Chargement…</p>}
          {playersError && <p className="admin-error-text">{playersError}</p>}
          {!loadingPlayers && !playersError && matches.length === 0 && (
            <p className="admin-muted">Aucun joueur pour cette séquence.</p>
          )}
          {matches.length > 0 && (
            <div className="differe-stats-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Joueur</th>
                    <th>Rang donne</th>
                    <th>Score</th>
                    <th>Contrat</th>
                    <th>Cartes humaines (pli 1→{depth})</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {matches.map(m => (
                    <tr key={m.resultId}>
                      <td>{m.pseudoDisplay || m.pseudo}</td>
                      <td>{formatPerDealRankLabel(m.perDealRank, m.perDealRankExAequo)}</td>
                      <td>{m.dealScore}</td>
                      <td>{m.contractLabel}</td>
                      <td className="differe-stats-top-cells">
                        {m.humanCards.map((c, i) => (
                          <DiffereStatsCardChip key={i} card={c} led={m.humanLed?.[i] === true} />
                        ))}
                      </td>
                      <td>
                        <button type="button" className="admin-btn admin-btn-sm" onClick={() => void openDealDetail(m)}>
                          18 plis
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <DiffereStatsDealDetailModal
        open={loadingDetail || studiedDetail != null || detailError != null}
        loading={loadingDetail}
        error={detailError}
        studiedDetail={studiedDetail}
        compareDetail={compareDetail}
        hypothesisCards={detailHypothesisCards}
        trickFocus={detailTrickFocus}
        diffSeatKeys={detailDiffSeatKeys}
        sessionPseudo={sessionPseudo}
        topX={topX}
        contractTypes={contractTypes}
        pageEquivMode={equivMode}
        lowRankLimit={lowRankLimit}
        differeMeta={differeMeta}
        onClose={closeDealDetailModal}
        onStudiedDetail={setStudiedDetail}
        onCompareDetail={setCompareDetail}
        onHypothesisCards={setDetailHypothesisCards}
        onTrickFocus={setDetailTrickFocus}
        onDiffSeatKeys={setDetailDiffSeatKeys}
        onError={setDetailError}
        onLoading={setLoadingDetail}
      />
    </>
  );
}
